import { computed, DestroyRef, Directive, effect, inject, input, TemplateRef, untracked } from '@angular/core';
import { EditDataService } from '../../../services/_edit-data.service';
import { MediaLookPanelService } from '../../../services/media-look-panel.service';
import { RAIL_MENU_HOST } from './rail-menu-host';

/** Um dono por instância: é por ele que o painel sabe de qual card é o conteúdo. */
let nextOwnerId = 0;

/**
 * Botão de design (a paleta) do rail de qualquer campo: abre o painel da esquerda em modo
 * foco (MediaLookPanelService), com a prévia do PDF acesa e rolada até o item, em vez de
 * um menu flutuante preso ao rail que cobria o próprio card.
 *
 * O conteúdo é um template do card (`[raLookPanel]`): desenhado no painel, mas os valores
 * e as escolhas seguem ligados ao card, e o SCSS dele continua valendo lá.
 *
 * Também atende o "Configurações deste item" da prévia (`itemConfigRequestId`), como o
 * botão Foto/Grade/Vídeo das mídias (media-look-chip, que tem a mesma ligação).
 *
 * Uso, no botão do rail:
 * `<button [raLookPanel]="tpl" lookPanelTitle="..." [lookPanelItemId]="id" #look="lookPanel" (click)="look.toggle()">`
 */
@Directive({
    selector: '[raLookPanel]',
    exportAs: 'lookPanel',
    host: { '[attr.aria-pressed]': 'isOpen()' },
})
export class RaLookPanelDirective {
    /** O conteúdo do painel. */
    public template = input.required<TemplateRef<unknown>>({ alias: 'raLookPanel' });
    /** Chave do título do cabeçalho do painel (o mesmo nome do botão). */
    public titleKey = input.required<string>({ alias: 'lookPanelTitle' });
    /** Ícone do cabeçalho do painel. */
    public icon = input<string>('palette', { alias: 'lookPanelIcon' });
    /** O item do card: a prévia rola até ele quando o painel abre. */
    public itemId = input<string | null | undefined>(null, { alias: 'lookPanelItemId' });
    public disabled = input<boolean>(false, { alias: 'lookPanelDisabled' });

    private _panel = inject(MediaLookPanelService);
    private _data = inject(EditDataService);
    private _host = inject(RAIL_MENU_HOST, { optional: true });
    private readonly _ownerId = `look-panel-${++nextOwnerId}`;

    /** O painel está aberto com o conteúdo deste card (acende o botão do rail). */
    public isOpen = computed(() => this._panel.isOpenFor(this._ownerId));

    constructor() {
        // Com o painel aberto para este card, o rail dele fica à vista: é o card sendo
        // ajustado, e o botão aceso mostra de quem é o painel.
        effect(onCleanup => {
            if (!this.isOpen() || !this._host) return;
            const host = this._host;
            untracked(() => host.lookPanelOpen.set(true));
            onCleanup(() => host.lookPanelOpen.set(false));
        });

        // "Configurações deste item" na prévia: o card dono abre o painel e limpa o pedido,
        // senão voltar para a seção mais tarde reabriria o painel do nada.
        effect(() => {
            const requested = this._data.itemConfigRequestId();
            if (!requested || requested !== this.itemId()) return;

            untracked(() => {
                this.open();
                this._data.clearItemConfigRequest();
            });
        });

        // O card saiu da tela (outra seção, campo removido): o painel dele fecha, a menos que
        // outro card o assuma em seguida (MediaLookPanelService.release).
        inject(DestroyRef).onDestroy(() => this._panel.release(this._ownerId));
    }

    /** O botão do rail abre o painel com este card, ou fecha se já é dele. */
    public toggle(): void {
        if (this.isOpen()) {
            this._panel.close(this._ownerId);
            return;
        }

        this.open();
    }

    public open(): void {
        if (this.disabled() || this.isOpen()) return;

        this._panel.open({
            ownerId: this._ownerId,
            itemId: this.itemId(),
            titleKey: this.titleKey(),
            icon: this.icon(),
            template: this.template(),
        });
    }
}
